import "../shell/theme.css";
import "../platform/diagnostics/diagnostics.css";
import type { GameEvent, GameManifest } from "../contracts/game";
import type { SystemSurfaceId, SystemSurfaceManifest } from "../contracts/system-surface";
import { GameRegistry } from "./game-registry";
import { LOCAL_GAME_MANIFESTS } from "./local-games";
import { CATALOG_BASELINE_TIMESTAMP, externalCatalogEntries, firstPartyCatalogEntries } from "./catalog-entries";
import { GameHost } from "./game-host";
import { SystemSurfaceHost } from "./system-surface-host";
import { createHomeSlotsForState, rankExternalGamesByFrequency } from "./home-order";
import { loadExternalTapCounts, recordExternalTap } from "../platform/storage/external-taps";
import { ROSEBUD_CATALOG, type ExternalGameLink } from "./rosebud-catalog";
import { CURATED_WEB_EXTERNALS, curatedWebLaunchMode } from "./curated-web-catalog";
import { launchExternalGame } from "./external-game-launch";
import { initialAppState, reduceAppState, type AppState } from "./app-state";
import { probeRequiredCapabilities } from "./runtime-capabilities";
import { recordGameEvent, recordLaunch, recordPlayDuration } from "./usage-stats";
import { createSurfaceManifest } from "../creator/create-manifest";
import { AudioManager, createBrowserAudioContext, type BrowserAudioScope } from "../platform/audio/audio-service";
import { DiagnosticsService } from "../platform/diagnostics/diagnostics-service";
import { installDiagnosticsAccess, type DiagnosticsAccessOptions } from "../platform/diagnostics/diagnostics-view";
import { PlatformServices } from "../platform/platform-services";
import { createStorageAdapter, type StorageLike } from "../platform/storage/storage-adapter";
import { ViewportService } from "../platform/viewport/viewport-service";
import { ExternalOverlay } from "../shell/external-overlay";
import { HomeView } from "../shell/home-view";
import { ShellView } from "../shell/shell-view";
import { systemSurfaceForPath } from "./launch-path";

export interface ArcadeApp { destroy(): void; }

const SYSTEM_SURFACES: ReadonlyMap<SystemSurfaceId, SystemSurfaceManifest> = new Map([
  [createSurfaceManifest.id, createSurfaceManifest],
]);

// The legacy launcher contract is still generated from Rosebud and therefore
// types its URL as rosebud.ai-only. Curated web externals are deliberately
// kept separate from that generated source; this one boundary cast lets the
// existing launcher render them without pretending they are Rosebud records.
const EXTERNAL_CATALOG: readonly ExternalGameLink[] = [
  ...CURATED_WEB_EXTERNALS.map((link) => link as unknown as ExternalGameLink),
  ...ROSEBUD_CATALOG,
];

// Static source data; computed once at module load, not per render. Curated
// non-Rosebud externals are not yet category-adapter entries; they surface in
// the ranked external lane until the catalog source contract is generalized.
const CATALOG_ENTRIES = [
  ...firstPartyCatalogEntries(LOCAL_GAME_MANIFESTS, CATALOG_BASELINE_TIMESTAMP),
  ...externalCatalogEntries(ROSEBUD_CATALOG, CATALOG_BASELINE_TIMESTAMP),
];

export function createApp(root: HTMLElement, scope: Window = window): ArcadeApp {
  const view = new ShellView(root);
  const diagnostics = new DiagnosticsService();
  let diagnosticsAccess: ReturnType<typeof installDiagnosticsAccess> | undefined;
  let homeView: HomeView | undefined;
  let state: AppState = initialAppState;
  let destroyed = false;
  let activeGameId: string | null = null;
  let activeGameStartedAt: number | null = null;
  view.renderBoot();

  const capabilities = probeRequiredCapabilities(scope);
  if (!capabilities.canStart) {
    view.renderUnsupported();
    return { destroy() { destroyed = true; view.destroy(); } };
  }

  let browserStorage: StorageLike | undefined;
  if (capabilities.persistentStorage) {
    try { browserStorage = scope.localStorage; } catch { browserStorage = undefined; }
  }

  const storage = createStorageAdapter(browserStorage);
  const persistedState = storage.load();
  const registry = new GameRegistry(LOCAL_GAME_MANIFESTS);
  // Use the actual app window rather than ambient global state so legacy
  // embedded browsers can take the prefixed Web Audio constructor path.
  const audioManager = new AudioManager(() => createBrowserAudioContext(scope as unknown as BrowserAudioScope));
  audioManager.setPreferences({
    enabled: persistedState.preferences.soundEnabled,
    masterVolume: persistedState.preferences.masterVolume,
  });
  const removeAudioGestureRetries = audioManager.bindGestureRetries(scope.document);
  const viewport = new ViewportService(scope);
  const platform = new PlatformServices({ scope, viewport, diagnostics, audioManager });

  const finishSessionAccounting = (): void => {
    if (activeGameId === null || activeGameStartedAt === null) return;
    const elapsed = Math.max(0, scope.performance.now() - activeGameStartedAt);
    const gameId = activeGameId;
    activeGameId = null;
    activeGameStartedAt = null;
    storage.mutate((draft) => recordPlayDuration(draft, gameId, elapsed));
  };

  const persistGameEvent = (gameId: string, event: GameEvent): void => {
    if (event.type !== "match-started" && event.type !== "match-completed") return;
    storage.mutate((draft) => recordGameEvent(draft, gameId, event, Date.now()));
  };

  let gameHost!: GameHost;
  let systemSurfaceHost!: SystemSurfaceHost;

  const installDiagnostics = (): void => {
    diagnosticsAccess?.destroy();
    diagnosticsAccess = undefined;
    const trigger = root.querySelector<HTMLElement>("[data-version='true']");
    if (!trigger) return;
    const options: DiagnosticsAccessOptions = {
      root,
      trigger,
      document: scope.document,
      scope,
      diagnostics,
      getContext: () => ({
        buildId: "development",
        appVersion: "0.1.0",
        renderScale: viewport.snapshot.renderScale,
        canvasBacking: null,
        storageMode: storage.isPersistent ? "persistent" : "memory",
        audioState: audioManager.runningContext?.state ?? "not-running",
        serviceWorkerState: capabilities.serviceWorker ? "available" : "unsupported",
        lifecycleState: state.kind,
        currentGame: activeGameId,
      }),
    };
    diagnosticsAccess = installDiagnosticsAccess(options);
  };

  const renderHome = (): void => {
    if (destroyed) return;
    diagnosticsAccess?.destroy();
    diagnosticsAccess = undefined;
    homeView?.destroy();
    const homeHost = view.renderHome();
    homeView = new HomeView(homeHost, scope.document);
    homeView.render(
      createHomeSlotsForState(registry, storage.snapshot(), 10),
      (gameId) => { void openGame(gameId); },
      (surfaceId) => { void openSystemSurface(surfaceId); },
      rankExternalGamesByFrequency(EXTERNAL_CATALOG, loadExternalTapCounts(browserStorage)),
      (link) => { recordExternalTap(browserStorage, link.slug); openExternalGame(link); },
      CATALOG_ENTRIES,
    );
    installDiagnostics();
  };

  const renderLegal = (): void => {
    if (destroyed) return;
    diagnosticsAccess?.destroy();
    diagnosticsAccess = undefined;
    homeView?.destroy();
    homeView = undefined;
    view.renderLegal();
  };

  const renderRecovery = (gameId: string, error: Error): void => {
    if (destroyed) return;
    diagnosticsAccess?.destroy();
    diagnosticsAccess = undefined;
    homeView?.destroy();
    homeView = undefined;
    view.renderRecovery(gameId, error, () => {
      if (destroyed) return;
      state = reduceAppState(state, { type: "recover-home" });
      renderHome();
    });
  };

  const renderSystemSurfaceRecovery = (surfaceId: SystemSurfaceId, error: Error): void => {
    if (destroyed) return;
    diagnosticsAccess?.destroy();
    diagnosticsAccess = undefined;
    homeView?.destroy();
    homeView = undefined;
    view.renderSystemSurfaceRecovery(surfaceId, error, () => {
      if (destroyed) return;
      state = reduceAppState(state, { type: "recover-home" });
      renderHome();
    });
  };

  const returnHome = async (): Promise<void> => {
    if (destroyed) return;
    await Promise.all([gameHost.close(), systemSurfaceHost.close()]);
    finishSessionAccounting();
    state = reduceAppState(
      state,
      state.kind === "recovering" || state.kind === "recovering-system-surface"
        ? { type: "recover-home" }
        : { type: "return-home" },
    );
    if (scope.location.pathname !== "/") scope.history.pushState({}, "", "/");
    renderHome();
  };

  gameHost = new GameHost(platform, {
    onRunning: (gameId) => {
      if (destroyed) return;
      state = reduceAppState(state, { type: "game-ready", gameId });
      activeGameId = gameId;
      activeGameStartedAt = scope.performance.now();
      storage.mutate((draft) => recordLaunch(draft, gameId));
      gameHost.resize(viewport.snapshot);
    },
    onEvent: (gameId, event) => {
      if (destroyed) return;
      persistGameEvent(gameId, event);
      if (event.type === "recoverable-error") {
        diagnostics.recordError(new Error(`${event.code}: recoverable game error`));
      }
    },
    onRequestHome: () => { if (!destroyed) void returnHome(); },
    onError: (gameId, error) => {
      if (destroyed) return;
      diagnostics.recordError(error);
      finishSessionAccounting();
      state = reduceAppState(state, { type: "game-failed", gameId, error });
      if (state.kind === "recovering") renderRecovery(gameId, error);
    },
  });

  systemSurfaceHost = new SystemSurfaceHost({
    onRunning: (surfaceId) => {
      if (destroyed) return;
      state = reduceAppState(state, { type: "system-surface-ready", surfaceId });
    },
    onRequestHome: () => { if (!destroyed) void returnHome(); },
    onError: (surfaceId, error) => {
      if (destroyed) return;
      diagnostics.recordError(error);
      state = reduceAppState(state, { type: "system-surface-failed", surfaceId, error });
      if (state.kind === "recovering-system-surface") renderSystemSurfaceRecovery(surfaceId, error);
    },
  });

  let closeExternalRequested = false;
  const requestCloseExternalGame = (): void => {
    if (!externalOverlay.isOpen() || closeExternalRequested) return;
    closeExternalRequested = true;
    scope.history.back();
  };
  const externalOverlay = new ExternalOverlay(
    { onRequestClose: requestCloseExternalGame },
    scope.document,
  );

  const openExternalGame = (link: ExternalGameLink): void => {
    if (destroyed || state.kind !== "home") return;
    launchExternalGame(link, curatedWebLaunchMode(link.slug) ?? "frame", {
      navigate: (url) => scope.location.assign(url),
      pushTransientHistory: (slug) => {
        // Framed games are transient overlays on the same pathname; back and
        // close consume this entry. Top-level navigation deliberately skips
        // it so browser Back returns directly to the existing CARCADE page.
        scope.history.pushState({ externalGame: slug }, "", scope.location.pathname);
      },
      frame: () => externalOverlay.open(link),
    });
  };

  const openManifest = async (manifest: GameManifest): Promise<void> => {
    if (destroyed || state.kind !== "home") return;
    const gameId = manifest.id;
    state = reduceAppState(state, { type: "select-game", gameId });
    diagnosticsAccess?.destroy();
    diagnosticsAccess = undefined;
    homeView?.destroy();
    homeView = undefined;
    const host = view.renderLoading(gameId);
    installDiagnostics();
    void audioManager.unlockFromGesture();
    const opened = await gameHost.open(manifest, host);
    if (opened && !destroyed && state.kind === "running-game") {
      view.revealGame(gameId, host);
      return;
    }
    if (!opened && !destroyed && state.kind === "loading-game") {
      state = reduceAppState(state, { type: "return-home" });
      renderHome();
    }
  };

  const openGame = async (gameId: string): Promise<void> => {
    const manifest = registry.get(gameId);
    if (!manifest || manifest.status !== "playable") return;
    await openManifest(manifest);
  };

  const openSystemSurface = async (surfaceId: SystemSurfaceId): Promise<void> => {
    if (destroyed || state.kind !== "home") return;
    const manifest = SYSTEM_SURFACES.get(surfaceId);
    if (!manifest) return;
    const path = surfaceId === "create" ? "/create" : "/";
    if (scope.location.pathname !== path) scope.history.pushState({}, "", path);
    state = reduceAppState(state, { type: "select-system-surface", surfaceId });
    diagnosticsAccess?.destroy();
    diagnosticsAccess = undefined;
    homeView?.destroy();
    homeView = undefined;
    const host = view.renderSystemSurfaceLoading(surfaceId, manifest.title);
    installDiagnostics();
    const opened = await systemSurfaceHost.open(manifest, host);
    if (opened && !destroyed && state.kind === "running-system-surface") {
      view.revealSystemSurface(surfaceId, host);
      return;
    }
    if (!opened && !destroyed && state.kind === "loading-system-surface") {
      state = reduceAppState(state, { type: "return-home" });
      renderHome();
    }
  };

  const unsubscribeViewport = viewport.subscribe((snapshot) => gameHost.resize(snapshot));
  const onVisibilityChange = (): void => {
    if (scope.document.visibilityState === "hidden") gameHost.pause("visibility-hidden");
    else gameHost.resume();
  };
  scope.document.addEventListener("visibilitychange", onVisibilityChange);
  state = reduceAppState(state, { type: "boot-ready" });
  renderHome();

  const onPopState = (): void => {
    if (destroyed) return;
    if (externalOverlay.isOpen()) {
      externalOverlay.close();
      closeExternalRequested = false;
      return;
    }
    if (scope.location.pathname === "/legal") {
      renderLegal();
      return;
    }
    const surfaceId = systemSurfaceForPath(scope.location.pathname);
    if (surfaceId && state.kind === "home") void openSystemSurface(surfaceId);
    if (!surfaceId && state.kind === "running-system-surface") void returnHome();
  };
  scope.addEventListener("popstate", onPopState);
  if (scope.location.pathname === "/legal") {
    renderLegal();
  } else {
    const initialSurface = systemSurfaceForPath(scope.location.pathname);
    if (initialSurface) void openSystemSurface(initialSurface);
  }

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      diagnosticsAccess?.destroy();
      homeView?.destroy();
      externalOverlay.close();
      removeAudioGestureRetries();
      unsubscribeViewport();
      scope.document.removeEventListener("visibilitychange", onVisibilityChange);
      scope.removeEventListener("popstate", onPopState);
      viewport.destroy();
      void Promise.all([gameHost.close(), systemSurfaceHost.close()]).finally(() => finishSessionAccounting());
      view.destroy();
    },
  };
}
